import { useNavigate } from "@tanstack/react-router";
import { useMemo, useRef } from "react";
import { useHomeDistribution } from "@/api/home";
import { useGmvFormatter } from "@/hooks/use-gmv-formatter";
import { useScopedUrl } from "@/hooks/use-scoped-url";
import { ASK_TITLE, type AskRequest, askPromptsFor } from "../lib/ask-prompts";
import { type DistributionRange, headingFor, type Selection } from "../lib/ranges";
import { useStuck } from "../lib/use-stuck";
import { ActiveCreatorsCard, BrandSafetyCard, ChannelPerformanceCard, VideosPostedCard } from "./cards";
import { GmvBreakdownCard } from "./gmv-breakdown-card";
import { RangeToggle } from "./range-toggle";
import { StatCardSkeleton } from "./stat-card";

const STUCK_SHADOW = "0 6px 16px -8px rgba(33,19,58,.14), 0 2px 6px -2px rgba(33,19,58,.06)";

type DistributionSectionProps = {
  onAsk: (request: AskRequest) => void;
  range: DistributionRange;
  rangeReady: boolean;
  maxSelectableDate: string;
  onSelectionChange: (selection: Selection) => void;
};

export function DistributionSection({
  onAsk,
  range,
  rangeReady,
  maxSelectableDate,
  onSelectionChange,
}: DistributionSectionProps) {
  const sentinel = useRef<HTMLDivElement>(null);
  const stuck = useStuck(sentinel);
  const formatGmv = useGmvFormatter();
  const scopedUrl = useScopedUrl();
  const navigate = useNavigate();

  const { data, isLoading } = useHomeDistribution({ from: range.from, to: range.to }, { enabled: rangeReady });

  const heading = useMemo(() => headingFor(range), [range]);

  const goTo = (path: string, search: Record<string, string>) => navigate({ to: `${scopedUrl}${path}`, search });

  return (
    <div className="flex w-full flex-col pt-10 pb-10">
      <div ref={sentinel} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />

      {/* The bar spans the scroll container while its contents stay on the same grid as
          the cards, so the background reaches the edges of the page rather than stopping
          at the centred content column. */}
      <div
        style={{ boxShadow: stuck ? STUCK_SHADOW : "none", transition: "box-shadow 200ms ease" }}
        className="sticky top-0 z-30 w-full bg-primary"
      >
        <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center justify-between gap-4 px-28 py-3">
          <h2 className="text-[28px] leading-9 font-semibold tracking-[-0.01em] text-[#21133a]">{heading}</h2>
          <RangeToggle range={range} maxDate={maxSelectableDate} onChange={onSelectionChange} />
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-8 px-28 pt-6">
        <div className="grid grid-cols-1 gap-5 min-[1000px]:grid-cols-2">
          {isLoading || !data ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <ChannelPerformanceCard
                key={`channel-${range.from}-${range.to}`}
                data={data}
                range={range}
                onAsk={onAsk}
                formatGmv={formatGmv}
                onFooter={() => onAsk({ suggestions: askPromptsFor("channel", range), title: ASK_TITLE.channel })}
              />
              <BrandSafetyCard
                key={`safety-${range.from}-${range.to}`}
                data={data}
                range={range}
                onAsk={onAsk}
                onFooter={() =>
                  goTo("/safety/flagged", {
                    tab: "needs-review",
                    flaggedDateStart: range.from,
                    flaggedDateEnd: range.to,
                  })
                }
              />
              <ActiveCreatorsCard
                key={`creators-${range.from}-${range.to}`}
                data={data}
                range={range}
                onAsk={onAsk}
                onFooter={() =>
                  goTo("/creators", {
                    classification: "__all",
                    startDate: range.from,
                    endDate: range.to,
                    postedFrom: range.from,
                    postedTo: range.to,
                  })
                }
              />
              <VideosPostedCard
                key={`videos-${range.from}-${range.to}`}
                data={data}
                range={range}
                onAsk={onAsk}
                onFooter={() =>
                  goTo("/videos", {
                    startDate: range.from,
                    endDate: range.to,
                    publishDateStart: range.from,
                    publishDateEnd: range.to,
                  })
                }
              />
            </>
          )}
        </div>

        <GmvBreakdownCard range={range} rangeReady={rangeReady} onAsk={onAsk} />
      </div>
    </div>
  );
}
